import OpenSeadragon from './openseadragon';
import { setCaptureBtn } from './captureBtn';
import { setScalebar } from './openseadragon-scalebar';
import { setRotationController } from './rotationController';
import { get, set, clean, getStore, getOverlay } from './openseadragon-annotations'; // singleton Store 제거 후 getStore로 변경 (듀얼 뷰어 기능 위해)
import './openseadragon-filtering';
import { createCustomPrompt } from './customPrompt';

export const createOpenSeadragon = ({
  id = 'openseadragon',
  showScalebar = false,
  useScaleBar = true,
  useMagnificationBar = true,
  saveAnnotation = false,
  useCapture = true,
  getSlideMpp = () => {},
  getAnnotations = () => {},
  addAnnotation,
  annotationClickEvent,
  useRotationSlider = false,
  useFiltering = false,
  showNavigator = true,
  isAnno = true,
  enableRulerMeasurement = true,
  isPlainImage = false,
  useInvertedZoom = false,
  slideTitle = '',
  useZoomSpeedSlider = true,
  defaultZoomSpeed = 3.5,
}) => {
  const runtimeOptions = {
    showScalebar,
    useScaleBar,
    useMagnificationBar,
    useCapture,
    useRotationSlider,
    useFiltering,
    showNavigator,
    useInvertedZoom,
    useZoomSpeedSlider,
    zoomSpeed: defaultZoomSpeed,
    slideTitle,
    gammaValue: 1,
  };

  if (showScalebar) setScalebar(OpenSeadragon);
  setCaptureBtn(OpenSeadragon);
  setRotationController(OpenSeadragon);

  const getZoomFactor = (speed = runtimeOptions.zoomSpeed) => {
    const normalizedSpeed = Number.isFinite(Number(speed))
      ? Math.min(5, Math.max(1, Number(speed)))
      : 3.5;
    return normalizedSpeed / 10;
  };

  const viewer = OpenSeadragon({
    id,
    crossOriginPolicy: 'Anonymous',
    animationTime: 0.2,
    blendTime: 0.1,
    springStiffness: 1,
    showNavigator,
    navigatorPosition: 'ABSOLUTE',
    navigatorTop: '45px',
    navigatorLeft: '4px',
    navigatorHeight: '160px',
    navigatorWidth: '160px',
    zoomPerScroll: useInvertedZoom
      ? Math.pow(10, -getZoomFactor(defaultZoomSpeed))
      : Math.pow(10, getZoomFactor(defaultZoomSpeed)),
    showRotationControl: true,
    visibilityRatio: 0.2,
    imageLoaderLimit: 6,
    maxImageCacheCount: 200,
    gestureSettingsMouse: {
      clickToZoom: false,
    },
    maxZoomLevel: isPlainImage ? 40 : null,
    zoomPerClick: 1.2,
  });

  viewer.toggleNavigator = function () {
    if (this.navigator) {
      const labelDiv = document.getElementById(`${id}-navigator-label`);
      const zoomSliderDiv = document.getElementById(`${id}-navigator-slider`);
      if (this.navigator.element.style.display === 'none') {
        this.navigator.element.style.display = 'block';
        if (labelDiv) labelDiv.style.display = 'block';
        if (zoomSliderDiv && runtimeOptions.useZoomSpeedSlider) {
          zoomSliderDiv.style.display = 'flex';
        }
      } else {
        this.navigator.element.style.display = 'none';
        if (labelDiv) labelDiv.style.display = 'none';
        if (zoomSliderDiv) zoomSliderDiv.style.display = 'none';
      }
    }
  };

  viewer.canvas.style.outline = 'none';
  if (viewer.areAnnotationsActive()) {
    viewer.shutdownAnnotations();
  }

  viewer.canvas.style.outline = 'none';
  if (isAnno) viewer.initializeAnnotations();

  if (showScalebar) {
    getSlideMpp().then((data) => { viewer.slideMpp = data; });
  } else {
    viewer.slideMpp = 1;
  }

  viewer.getAllAnnotations = () => get(viewer);

  viewer.setAnnotation = (pathList) => {
    if (viewer.areAnnotationsActive()) {
      const normalizedPathList = Array.isArray(pathList)
        ? pathList.filter((path) => Array.isArray(path) && path[1] && typeof path[1] === 'object')
        : [];
      for (const path of normalizedPathList) path[1].onClick = annotationClickEvent;
      clean(viewer);
      set(viewer, normalizedPathList);
    }
  };

  viewer.cleanAnnotations = () => {
    if (viewer.areAnnotationsActive()) {
      clean(viewer);
    }
  };

  // 어노테이션 SVG 를 현재 화면 그대로(회전 포함) 캡처 캔버스에 그린다.
  // 화면에 보이는 영역만 viewBox 로 잘라 그리므로 깊게 확대해도 SVG 크기가 커지지 않는다.
  // 그리지 못하면 이유를 남기고 false 를 돌려준다 (조용히 빠지지 않도록).
  const drawAnnotationsOnto = (ctx, pixelRatio) => {
    const skip = (reason) => {
      console.warn(`Annotation capture skipped: ${reason}`);
      return Promise.resolve(false);
    };
    const svg = getOverlay(viewer);
    const item = viewer.world && viewer.world.getItemAt(0);
    const dims = (item && item.source && item.source.dimensions) || (viewer.source && viewer.source.dimensions);
    if (!svg) return skip('annotation overlay not found');
    if (!dims || !viewer.viewport) return skip('image not opened');

    const { viewport, container } = viewer;
    // 뷰어 화면 좌표 <-> 어노테이션 좌표(이미지 기준 0~100)
    // 이미지가 여러 장 열려 있어도 정확하도록 viewport 대신 첫 번째 TiledImage 기준으로 변환
    const toImage = (vp) => (item ? item.viewportToImageCoordinates(vp) : viewport.viewportToImageCoordinates(vp));
    const toViewport = (pt) => (item ? item.imageToViewportCoordinates(pt) : viewport.imageToViewportCoordinates(pt));
    const toPercent = (px, py) => {
      const vp = viewport.pointFromPixel(new OpenSeadragon.Point(px, py), true);
      const img = toImage(vp);
      return { x: (img.x / dims.x) * 100, y: (img.y / dims.y) * 100 };
    };
    const toPixel = (x, y) => viewport.pixelFromPoint(
      toViewport(new OpenSeadragon.Point((x / 100) * dims.x, (y / 100) * dims.y)),
      true,
    );

    const w = container.clientWidth;
    const h = container.clientHeight;
    const corners = [toPercent(0, 0), toPercent(w, 0), toPercent(w, h), toPercent(0, h)];
    const minX = Math.max(0, Math.min(...corners.map((c) => c.x)));
    const maxX = Math.min(100, Math.max(...corners.map((c) => c.x)));
    const minY = Math.max(0, Math.min(...corners.map((c) => c.y)));
    const maxY = Math.min(100, Math.max(...corners.map((c) => c.y)));
    if (!(maxX > minX && maxY > minY)) return skip('annotation area is outside the visible screen');

    // 잘라낸 영역의 화면상 원점과 x/y 축 벡터 (회전·반전이 그대로 반영됨)
    const origin = toPixel(minX, minY);
    const axisX = toPixel(maxX, minY).minus(origin);
    const axisY = toPixel(minX, maxY).minus(origin);
    const svgWidth = Math.hypot(axisX.x, axisX.y);
    const svgHeight = Math.hypot(axisY.x, axisY.y);
    if (!svgWidth || !svgHeight) return skip('visible annotation area has no size');

    const clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('viewBox', `${minX} ${minY} ${maxX - minX} ${maxY - minY}`);
    clone.setAttribute('width', String(svgWidth));
    clone.setAttribute('height', String(svgHeight));
    clone.removeAttribute('style');
    // 저장된 어노테이션 데이터에 XML 이름 규칙에 맞지 않는 속성(예: 화살표 그룹의 0="g")이 섞여 있으면
    // 브라우저 화면에는 그려져도 SVG 이미지로는 열리지 않으므로 캡처용 복제본에서만 제거
    const validAttrName = /^[A-Za-z_][\w.:-]*$/;
    [clone, ...clone.querySelectorAll('*')].forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        if (!validAttrName.test(attr.name)) el.removeAttribute(attr.name);
      });
    });
    // 라벨 등에 XML 에서 허용되지 않는 제어문자가 있으면 SVG 이미지 로드가 실패하므로 제거
    // eslint-disable-next-line no-control-regex
    const source = new XMLSerializer().serializeToString(clone).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        ctx.save();
        ctx.setTransform(
          (pixelRatio * axisX.x) / svgWidth,
          (pixelRatio * axisX.y) / svgWidth,
          (pixelRatio * axisY.x) / svgHeight,
          (pixelRatio * axisY.y) / svgHeight,
          pixelRatio * origin.x,
          pixelRatio * origin.y,
        );
        ctx.drawImage(img, 0, 0, svgWidth, svgHeight);
        ctx.restore();
        resolve(true);
      };
      img.onerror = () => {
        console.error('Annotation capture failed: SVG could not be rendered', source.slice(0, 2000));
        resolve(false);
      };
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`;
    });
  };

  // 현재 화면을 캔버스로 만든다. withAnnotations 면 어노테이션·라벨까지 포함.
  viewer.captureToCanvas = async ({ withAnnotations = false } = {}) => {
    const imgCanvas = viewer.drawer.canvas;
    const canvas = document.createElement('canvas');
    canvas.width = imgCanvas.width;
    canvas.height = imgCanvas.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgCanvas, 0, 0);

    const pixelRatio = imgCanvas.width / (viewer.container.clientWidth || imgCanvas.width);

    // 어노테이션을 요청했는데 그리지 못했으면 canvas.annotationsDrawn = false 로 알린다
    canvas.annotationsDrawn = false;
    if (withAnnotations) {
      if (viewer.areAnnotationsActive()) {
        canvas.annotationsDrawn = await drawAnnotationsOnto(ctx, pixelRatio);
      } else {
        console.warn('Annotation capture skipped: annotation plugin is not active');
      }
    }

    const scalebar = viewer.scalebarInstance;
    if (scalebar && scalebar.divElt && scalebar.divElt.style.display !== 'none') {
      const scaleCanvas = scalebar.getAsCanvas();
      const location = scalebar.getScalebarLocation();
      ctx.drawImage(
        scaleCanvas,
        location.x * pixelRatio,
        location.y * pixelRatio,
        scaleCanvas.width * pixelRatio,
        scaleCanvas.height * pixelRatio,
      );
    }

    return canvas;
  };

  viewer.downloadCapture = async ({ withAnnotations = false, filename = 'capture.png' } = {}) => {
    try {
      const canvas = await viewer.captureToCanvas({ withAnnotations });
      if (withAnnotations && !canvas.annotationsDrawn) {
        console.warn('Capture saved without annotations (see the warning above for the reason)');
      }
      const result = { annotationsDrawn: canvas.annotationsDrawn };
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }, 'image/png');
      return result;
    } catch (error) {
      console.error('Capture failed:', error);
      return null;
    }
  };

  viewer.slideTitle = slideTitle;
  viewer.navigatorLabelResizeObserver = null;
  viewer.navigatorLabelElement = null;
  viewer.navigatorZoomSliderElement = null;

  viewer.getLength = (x1, y1, x2, y2) => {
    const dimensions = viewer.source.dimensions;
    const dx = (x2 - x1) * dimensions.x / 100;
    const dy = (y2 - y1) * dimensions.y / 100;
    const lineLength = Math.sqrt(dx * dx + dy * dy) / 1000;
    return (lineLength * viewer.slideMpp).toFixed(2);
  };

  // viewer별 Store 참조 (어노테이션 모드 추적 등에 사용)
  // 초기화 전에는 null, initializeAnnotations 이후에 조회 가능
  Object.defineProperty(viewer, 'annotationsStore', {
    get() { return getStore(viewer); },
    configurable: true,
  });

  if (saveAnnotation) {
    // 그리는 동안 포인터가 지나간 화면상 범위(bounding box)를 추적해 둔다.
    // 종료 지점(click)과 합쳐 annotation 의 바운딩 박스를 만들어, 프롬프트가
    // 그리는 방향/형태와 무관하게 이 박스를 피해 배치되도록 한다.
    // ※ OSD 는 pointer 이벤트를 preventDefault 해 호환 mouse 이벤트를 억제하므로
    //    반드시 pointer 이벤트를, 그리고 캡처 단계로 받아야 확실히 잡힌다.
    let annoDragBox = null;
    viewer.canvas.addEventListener('pointerdown', (e) => {
      annoDragBox = { left: e.clientX, top: e.clientY, right: e.clientX, bottom: e.clientY };
    }, true);
    viewer.canvas.addEventListener('pointermove', (e) => {
      if (!annoDragBox || (e.buttons & 1) === 0) return; // 좌버튼 누른 채 이동 중일 때만
      annoDragBox.left = Math.min(annoDragBox.left, e.clientX);
      annoDragBox.top = Math.min(annoDragBox.top, e.clientY);
      annoDragBox.right = Math.max(annoDragBox.right, e.clientX);
      annoDragBox.bottom = Math.max(annoDragBox.bottom, e.clientY);
    }, true);

    viewer.canvas.addEventListener('click', async function (e) {
      const store = getStore(viewer);
      if (!store) return;
      const mode = store.getMode();
      if (mode === 'DRAW' || mode === 'DRAWRECT' || mode === 'DRAWELLIPSE' || mode === 'RULER' || mode === 'DRAWARROW') {
        const allAnno = get(viewer);
        let annotationName;
        let rulerValue = 0;
        // annotation 바운딩 박스를 넘겨, 프롬프트가 그림을 가리지 않게 한다.
        const box = annoDragBox || { left: e.clientX, top: e.clientY, right: e.clientX, bottom: e.clientY };
        const promptAnchor = {
          left: Math.min(box.left, e.clientX),
          top: Math.min(box.top, e.clientY),
          right: Math.max(box.right, e.clientX),
          bottom: Math.max(box.bottom, e.clientY),
        };
        if (mode === 'RULER' && enableRulerMeasurement) {
          const line = allAnno[allAnno.length - 1][1];
          rulerValue = viewer.getLength(line.x1, line.y1, line.x2, line.y2);
          annotationName = await createCustomPrompt(`길이 : ${rulerValue}mm`, promptAnchor);
        } else {
          annotationName = await createCustomPrompt('', promptAnchor);
        }
        if (annotationName !== null) {
          await addAnnotation(allAnno[allAnno.length - 1], annotationName, rulerValue);
        }
        clean(viewer);
        const pathList = await getAnnotations();
        if (pathList && Array.isArray(pathList) && pathList.length > 0) {
          viewer.setAnnotation(pathList);
        }
        viewer.stopDrawing();
      }
    });
  }

  if (useFiltering) {
    viewer.setFilterOptions({
      filters: {
        processors: [OpenSeadragon.Filters.GAMMA(1.0)],
      },
    });
  }

  const STANDARD_MAGNIFICATIONS = [1, 2, 4, 5, 10, 20, 40];
  const snapMagnification = (mag) =>
    STANDARD_MAGNIFICATIONS.reduce((prev, curr) =>
      (Math.abs(curr - mag) < Math.abs(prev - mag) ? curr : prev),
    );

  const ensureNavigatorLabel = () => {
    if (!viewer.navigator) {
      return null;
    }

    const navEl = viewer.navigator.element;
    navEl.style.overflow = 'visible';

    const labelId = `${id}-navigator-label`;
    let labelDiv = document.getElementById(labelId);

    if (!labelDiv) {
      labelDiv = document.createElement('div');
      labelDiv.id = labelId;
      labelDiv.style.position = 'absolute';
      labelDiv.style.bottom = '100%';
      labelDiv.style.left = '0';
      labelDiv.style.width = '100%';
      labelDiv.style.marginBottom = '5px';
      labelDiv.style.color = '#ffffff';
      labelDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
      labelDiv.style.padding = '4px 6px';
      labelDiv.style.borderRadius = '4px';
      labelDiv.style.fontSize = '12px';
      labelDiv.style.textAlign = 'center';
      labelDiv.style.pointerEvents = 'none';
      labelDiv.style.boxSizing = 'border-box';
      labelDiv.style.wordBreak = 'break-word';
      navEl.appendChild(labelDiv);
    }

    if (!viewer.navigatorLabelResizeObserver) {
      viewer.navigatorLabelResizeObserver = new ResizeObserver(() => {
        const labelHeight = labelDiv.offsetHeight;
        navEl.style.top = `${labelHeight > 0 ? labelHeight : 0}px`;
      });
      viewer.navigatorLabelResizeObserver.observe(labelDiv);
    }

    viewer.navigatorLabelElement = labelDiv;
    return labelDiv;
  };

  const updateZoomPerScroll = () => {
    const zoomFactor = getZoomFactor(runtimeOptions.zoomSpeed);
    viewer.zoomPerScroll = runtimeOptions.useInvertedZoom
      ? Math.pow(10, -zoomFactor)
      : Math.pow(10, zoomFactor);
  };

  const ensureNavigatorZoomSlider = () => {
    if (!viewer.navigator) {
      return null;
    }

    const navEl = viewer.navigator.element;
    navEl.style.overflow = 'visible';

    const sliderId = `${id}-navigator-slider`;
    let sliderContainer = document.getElementById(sliderId);

    if (!sliderContainer) {
      sliderContainer = document.createElement('div');
      sliderContainer.id = sliderId;
      sliderContainer.style.position = 'absolute';
      sliderContainer.style.top = '100%';
      sliderContainer.style.left = '0';
      sliderContainer.style.width = '100%';
      sliderContainer.style.marginTop = '5px';
      sliderContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      sliderContainer.style.border = '1px solid rgba(255, 255, 255, 0.3)';
      sliderContainer.style.borderRadius = '4px';
      sliderContainer.style.padding = '2px 5px 8px 5px';
      sliderContainer.style.boxSizing = 'border-box';
      sliderContainer.style.display = 'flex';
      sliderContainer.style.flexDirection = 'column';
      sliderContainer.style.gap = '6px';
      sliderContainer.style.alignItems = 'center';

      const stopEvent = (event) => event.stopPropagation();
      [
        'mousedown',
        'mouseup',
        'mousemove',
        'touchstart',
        'touchmove',
        'touchend',
        'pointerdown',
        'pointerup',
        'pointermove',
        'click',
        'dblclick',
      ].forEach((eventName) => {
        sliderContainer.addEventListener(eventName, stopEvent);
      });

      const speedLabel = document.createElement('div');
      speedLabel.dataset.role = 'zoom-speed-label';
      speedLabel.style.fontSize = '0.6rem';
      speedLabel.style.color = 'white';
      speedLabel.style.margin = '0';
      speedLabel.style.width = '100%';
      speedLabel.style.textAlign = 'left';
      speedLabel.style.pointerEvents = 'none';

      const speedSlider = document.createElement('input');
      speedSlider.dataset.role = 'zoom-speed-range';
      speedSlider.className = 'elem zoom-slider';
      speedSlider.type = 'range';
      speedSlider.min = '1';
      speedSlider.max = '5';
      speedSlider.step = '0.1';
      speedSlider.style.width = '100%';
      speedSlider.style.margin = '0';
      speedSlider.style.cursor = 'pointer';

      const updateTrack = (input) => {
        const min = Number(input.min);
        const max = Number(input.max);
        const pct = ((Number(input.value) - min) / (max - min)) * 100;
        input.style.background =
          `linear-gradient(to right, rgb(174, 203, 255) ${pct}%, rgba(255, 255, 255, 0.4) ${pct}%)`;
      };

      speedSlider.addEventListener('input', (event) => {
        const nextSpeed = Number(event.target.value);
        runtimeOptions.zoomSpeed = nextSpeed;
        speedLabel.innerHTML = `Zoom: ${nextSpeed.toFixed(1)}x`;
        updateTrack(event.target);
        updateZoomPerScroll();
      });

      sliderContainer.appendChild(speedLabel);
      sliderContainer.appendChild(speedSlider);
      navEl.appendChild(sliderContainer);
    }

    viewer.navigatorZoomSliderElement = sliderContainer;
    return sliderContainer;
  };

  const getScaleBar = (mpp) => {
    const REFERENCE_MPP = 0.263526;
    const REFERENCE_MAG = 40;

    const pixelsPerMeter = 1 / mpp * 1000000;
    viewer.slideMagnification = snapMagnification(REFERENCE_MAG * (REFERENCE_MPP / mpp));
    viewer.viewport.maxZoomLevel = null;
    const naturalMaxZoom = viewer.viewport.getMaxZoom();
    const maxZoomPixelRatio = viewer.maxZoomPixelRatio || 1.1;
    viewer.viewport.maxZoomLevel = (naturalMaxZoom / maxZoomPixelRatio) * 2;

    viewer.viewport.goHome(true);

    viewer.scalebar({
      type: OpenSeadragon.ScalebarType.MICROSCOPY,
      pixelsPerMeter,
      minWidth: '75px',
      location: OpenSeadragon.ScalebarLocation.BOTTOM_LEFT,
      xOffset: 80,
      yOffset: 10,
      color: 'rgb(150, 150, 150)',
      fontColor: 'rgb(100, 100, 100)',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      fontSize: 'small',
      barThickness: 2,
      magnification: viewer.slideMagnification,
      stayInsideImage: false,
      showScale: useScaleBar,
    });
    viewer.scalebarInstance.refresh({ showScale: useScaleBar, showMagnification: useMagnificationBar });
  };

  viewer.updateNavigatorLabel = (nextTitle = viewer.slideTitle) => {
    viewer.slideTitle = nextTitle || '';
    if (!viewer.navigator) {
      return;
    }

    const labelDiv = ensureNavigatorLabel();
    if (!labelDiv) {
      return;
    }

    labelDiv.style.display = runtimeOptions.showNavigator && viewer.slideTitle ? 'block' : 'none';
    labelDiv.innerText = viewer.slideTitle;
  };

  viewer.updateNavigatorZoomSlider = () => {
    const sliderContainer = ensureNavigatorZoomSlider();
    if (!sliderContainer) {
      return;
    }

    const speedLabel = sliderContainer.querySelector('[data-role="zoom-speed-label"]');
    const speedSlider = sliderContainer.querySelector('[data-role="zoom-speed-range"]');

    if (!speedLabel || !speedSlider) {
      return;
    }

    speedSlider.value = String(runtimeOptions.zoomSpeed);
    speedLabel.innerHTML = `Zoom: ${Number(runtimeOptions.zoomSpeed).toFixed(1)}x`;

    const min = Number(speedSlider.min);
    const max = Number(speedSlider.max);
    const pct = ((Number(speedSlider.value) - min) / (max - min)) * 100;
    speedSlider.style.background =
      `linear-gradient(to right, rgb(174, 203, 255) ${pct}%, rgba(255, 255, 255, 0.4) ${pct}%)`;

    sliderContainer.style.display =
      runtimeOptions.showNavigator && runtimeOptions.useZoomSpeedSlider ? 'flex' : 'none';
  };

  viewer.updateRuntimeOptions = (nextOptions = {}) => {
    Object.assign(runtimeOptions, nextOptions);
    if (Object.prototype.hasOwnProperty.call(nextOptions, 'slideTitle')) {
      viewer.slideTitle = nextOptions.slideTitle || '';
    }

    updateZoomPerScroll();

    if (viewer.navigator) {
      viewer.navigator.element.style.display = runtimeOptions.showNavigator ? 'block' : 'none';
      viewer.updateNavigatorLabel(viewer.slideTitle);
      viewer.updateNavigatorZoomSlider();
    }

    if (viewer.captureInstance?.setVisible) {
      viewer.captureInstance.setVisible(runtimeOptions.useCapture);
    }

    if (viewer.sliderInstance?.setVisible) {
      viewer.sliderInstance.setVisible(runtimeOptions.useRotationSlider);
    }

    if (viewer.filterPluginInstance?.setVisible) {
      viewer.filterPluginInstance.setVisible(runtimeOptions.useFiltering);
    }

    if (runtimeOptions.showScalebar && viewer.scalebarInstance) {
      viewer.scalebarInstance.refresh({
        showScale: runtimeOptions.useScaleBar,
        showMagnification: runtimeOptions.useMagnificationBar,
      });
    }
  };

  viewer.setGammaValue = (value = 1) => {
    runtimeOptions.gammaValue = value;
    if (!runtimeOptions.useFiltering) {
      return;
    }

    if (!viewer.filterPluginInstance) {
      viewer.setFilterOptions({
        filters: {
          processors: [OpenSeadragon.Filters.GAMMA(value)],
        },
        loadMode: 'async',
        skipReset: true,
      });
    }

    if (viewer.filterPluginInstance?.setValue) {
      viewer.filterPluginInstance.setValue(value);
    }
  };

  viewer.refreshUiControls = () => {
    if (runtimeOptions.useCapture) {
      viewer.capture({ visible: true });
    } else if (viewer.captureInstance?.setVisible) {
      viewer.captureInstance.setVisible(false);
    }

    if (runtimeOptions.useRotationSlider) {
      viewer.slider({ visible: true });
    } else if (viewer.sliderInstance?.setVisible) {
      viewer.sliderInstance.setVisible(false);
    }

    if (runtimeOptions.useFiltering) {
      viewer.setFilterOptions({
        filters: {
          processors: [OpenSeadragon.Filters.GAMMA(runtimeOptions.gammaValue ?? 1)],
        },
        loadMode: 'async',
      });
      viewer.filterPluginInstance?.setVisible?.(true);
    } else if (viewer.filterPluginInstance?.setVisible) {
      viewer.filterPluginInstance.setVisible(false);
    }
  };

  viewer.addHandler('open', function () {
    if (viewer.areAnnotationsActive()) {
      viewer.reattachAnnotationOverlay();
    }

    Promise.resolve(getAnnotations()).then((pathList) => {
      if (pathList && Array.isArray(pathList) && pathList.length > 0) {
        viewer.setAnnotation(pathList);
      } else {
        viewer.cleanAnnotations();
      }
    });

    viewer.refreshUiControls();

    if (runtimeOptions.showScalebar) {
      getSlideMpp().then((data) => {
        if (data > 0) {
          getScaleBar(data);
        } else {
          getScaleBar(0.263526);
        }
      });
    }

    viewer.updateRuntimeOptions(runtimeOptions);
  });

  const originalDestroy = viewer.destroy.bind(viewer);
  viewer.destroy = () => {
    if (viewer.areAnnotationsActive()) {
      try {
        viewer.shutdownAnnotations();
      } catch (e) {
        console.warn('shutdownAnnotations failed:', e);
      }
    }

    if (viewer.navigatorLabelResizeObserver) {
      viewer.navigatorLabelResizeObserver.disconnect();
      viewer.navigatorLabelResizeObserver = null;
    }
    viewer.navigatorZoomSliderElement?.remove?.();
    viewer.captureInstance?.destroy?.();
    viewer.sliderInstance?.destroy?.();
    viewer.filterPluginInstance?.destroy?.();
    originalDestroy();
  };

  return viewer;
};
